import { supabase } from '@/integrations/supabase/client';
import { driveIngest } from '@/lib/driveIngest';
import type { Provider } from '@/lib/driveIngestLogic';

// Maps an integration_configs.integration_type to the edge function that
// handles its sync_all action.
export const CONNECTOR_FUNCTION: Record<string, string> = {
  jira: 'jira-connector',
  azure_devops: 'azuredevops-connector',
  servicenow: 'servicenow-connector',
  sharepoint: 'microsoft-graph-connector',
  planner: 'microsoft-graph-connector',
  outlook: 'microsoft-graph-connector',
  onedrive_documents: 'microsoft-graph-connector',
};

// Maps an integration_type to the `service` param microsoft-graph-connector's
// switch statement actually dispatches on, for cases where the two names
// diverge (onedrive_documents -> 'documents'). Types not listed here pass
// their integration_type straight through as `service` (see below).
const SERVICE_PARAM_OVERRIDES: Record<string, string> = {
  onedrive_documents: 'documents',
};

// Maps the `source` value stored on projects_sync/tasks_sync rows (and in
// integration_audit_events.details) back to the integration_type used by
// integration_configs and CONNECTOR_FUNCTION above. Inverse of the
// SYNCED_DATA_SOURCES table in supabase/functions/integration-credentials.
export const SOURCE_TO_INTEGRATION_TYPE: Record<string, string> = {
  jira: 'jira',
  azure_devops: 'azure_devops',
  servicenow: 'servicenow',
  sharepoint: 'sharepoint',
  microsoft_planner: 'planner',
  microsoft_outlook: 'outlook',
};

// Folder-scoped document ingestion is synced by drive-ingest (via the hub), not by a connector's sync_all.
const DRIVE_PROVIDER: Record<string, Provider> = {
  google_drive: 'google',
  microsoft_drive: 'microsoft',
};

export async function triggerIntegrationSync(integrationId: string, integrationType: string) {
  const driveProvider = DRIVE_PROVIDER[integrationType];
  if (driveProvider) return driveIngest.syncNow(driveProvider);   // { started: true }: runs in the background

  const functionName = CONNECTOR_FUNCTION[integrationType];
  if (!functionName) throw new Error(`Unsupported integration type "${integrationType}"`);

  const body: Record<string, unknown> = { action: 'sync_all', integrationId };
  if (['sharepoint', 'planner', 'outlook', 'onedrive_documents'].includes(integrationType)) {
    body.service = SERVICE_PARAM_OVERRIDES[integrationType] ?? integrationType;
  }

  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) throw error;
  return data;
}
